import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Discount delta cell (Story 1.5, Design Spec §4.6 / §5).
 *
 * Renders a `delta_pp` value as a signed percentage-point change:
 *   larger discount → `+X.X pp`   (buyer-favourable)
 *   smaller discount → `−X.X pp`
 *   no change        → `0.0 pp`
 *   null             → `—`        (new row, price_data_missing, absent tier 2)
 *
 * COLOUR IS KEYED TO DISCOUNT DIRECTION, NOT NUMERIC SIGN (Design Spec §5):
 * a LARGER discount (+pp) is favourable for the customer (lower effective
 * cost) and renders GREEN; a SMALLER discount (−pp) renders RED. A `+2.5 pp`
 * delta is GREEN — do NOT "correct" it to the intuitive red-for-positive.
 *
 * The sign and ` pp` unit are ALWAYS rendered alongside the colour, so colour
 * is never the sole signal (NFR8). The optional `breached` flag renders the
 * delta in the alert (smaller-discount) colour regardless of direction, for a
 * tier flagged by the outlier threshold (§4.1 companion treatment).
 *
 * Pure @Input() → render. No events, no CrudService/HTTP, no route. This is a
 * DISPLAY delta — not the editable pp-delta input helper (Stories 3.2/3.4).
 */
@Component({
  selector: 'app-discount-delta-cell',
  templateUrl: './discount-delta-cell.component.html',
  styleUrls: ['../pricing-status.css', './discount-delta-cell.component.css'],
  imports: [CommonModule],
  standalone: true,
})
export class DiscountDeltaCellComponent {
  /** Signed percentage-point delta, or null (new/missing/absent tier). */
  @Input() deltaPp: number | null = null;
  /** Tier name for the aria-label, e.g. 'Pour'. */
  @Input() tierName = '';
  /**
   * When true the tier breached the outlier threshold: render in the alert
   * colour regardless of direction (§4.1). Applying the row-level companion
   * treatments (amber row bg) is the consuming screen's job, not this one.
   */
  @Input() breached = false;
  /** Decimal places for the pp value. */
  @Input() precision = 1;

  /** MINUS SIGN (U+2212) for negative deltas; EM DASH for null. */
  readonly MINUS = '−';
  readonly EM_DASH = '—';

  /**
   * The delta, or null when it must render `—` — i.e. absent (null/undefined)
   * OR non-finite (NaN/Infinity). The DTO emits a finite number or null, so a
   * non-finite value is bad data: render the em-dash, never `NaN pp`.
   */
  private get value(): number | null {
    if (this.deltaPp === null || this.deltaPp === undefined || !Number.isFinite(this.deltaPp)) {
      return null;
    }
    return this.deltaPp;
  }

  /** Magnitude rounded to the column precision, e.g. "2.5". Caller guards null. */
  private get magnitude(): string {
    return Math.abs(this.value as number).toFixed(this.precision);
  }

  /**
   * Direction AFTER rounding. A sub-precision value (e.g. +0.04 at precision 1)
   * rounds to "0.0", so it is treated as no change — otherwise the sign and the
   * direction colour would contradict the displayed magnitude (AC2: 0.0 pp is
   * muted/flat). null ⇒ flat.
   */
  private get direction(): 'up' | 'down' | 'flat' {
    const v = this.value;
    if (v === null || parseFloat(this.magnitude) === 0) {
      return 'flat';
    }
    return v > 0 ? 'up' : 'down';
  }

  /** Displayed text: `+X.X pp` / `−X.X pp` / `0.0 pp` / `—`. */
  get display(): string {
    if (this.value === null) {
      return this.EM_DASH;
    }
    switch (this.direction) {
      case 'up':
        return `+${this.magnitude} pp`;
      case 'down':
        return `${this.MINUS}${this.magnitude} pp`;
      default:
        return `${(0).toFixed(this.precision)} pp`;
    }
  }

  /** Direction colour class. `breached` forces the alert colour (§4.1). */
  get colourClass(): string {
    if (this.value === null) {
      return 'pf-delta--flat';
    }
    if (this.breached) {
      return 'pf-delta--down';
    }
    switch (this.direction) {
      case 'up':
        return 'pf-delta--up';
      case 'down':
        return 'pf-delta--down';
      default:
        return 'pf-delta--flat';
    }
  }

  /** §7 aria-label, e.g. "Pour Discount change plus 2.5 percentage points". */
  get ariaLabel(): string {
    const prefix = this.tierName ? `${this.tierName} ` : '';
    if (this.value === null) {
      return `${prefix}Discount change not available`;
    }
    switch (this.direction) {
      case 'up':
        return `${prefix}Discount change plus ${this.magnitude} percentage points`;
      case 'down':
        return `${prefix}Discount change minus ${this.magnitude} percentage points`;
      default:
        return `${prefix}Discount unchanged`;
    }
  }
}
